import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../firebase/AuthProvider';
import Sprint from '../../../interfaces/sprint/Sprint';
import { getSprints, getClosedSprints } from '../../../services/sprintServices/SprintService';
import SprintsPageTable from './SprintsPageTable';

const SprintsListPage: React.FC = () => {
    const { currentUser } = useAuth();
    const [allSprints, setAllSprints] = useState<Sprint[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // State for sorting
    const [sortBy, setSortBy] = useState<string>('startDate');
    const [sortDesc, setSortDesc] = useState<boolean>(true);

    // State for pagination
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);

    useEffect(() => {
        const fetchSprints = async () => {
            if (!currentUser) return;
            setIsLoading(true);
            setError(null);
            try {
                const token = await currentUser.getIdToken();
                const [activeSprints] = await Promise.all([
                    getSprints(token),
                    // getClosedSprints(token)
                ]);
                
                const combinedSprints = [
                    ...(activeSprints || [])
                    // ...(closedSprints || [])
                ];

                setAllSprints(combinedSprints);
            } catch (err) {
                setError('Failed to fetch sprints.');
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSprints();
    }, [currentUser]);

    const handleSort = (column: string) => {
        if (sortBy === column) {
            setSortDesc(!sortDesc);
        } else {
            setSortBy(column);
            setSortDesc(false);
        }
    };

    const processedSprints = useMemo(() => {
        let sortedSprints = [...allSprints];

        if (sortBy) {
            sortedSprints.sort((a, b) => {
                const aValue = a[sortBy as keyof Sprint];
                const bValue = b[sortBy as keyof Sprint];

                if (aValue === null || aValue === undefined) return 1;
                if (bValue === null || bValue === undefined) return -1;

                if (aValue < bValue) return sortDesc ? 1 : -1;
                if (aValue > bValue) return sortDesc ? -1 : 1;
                return 0;
            });
        }

        return sortedSprints;
    }, [allSprints, sortBy, sortDesc]);

    const paginatedSprints = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return processedSprints.slice(startIndex, startIndex + pageSize);
    }, [processedSprints, currentPage, pageSize]);

    if (error) {
        return <div className="alert alert-danger">{error}</div>;
    }

    return (
        <div className="tasks_page_container shadow-sm p-3">
            <div className="top top_bar_flx">
                <div className="top_bar_tittle">
                    <h2>Sprints</h2>
                </div>
            </div>
            <SprintsPageTable
                sprints={paginatedSprints}
                isLoading={isLoading}
                page={currentPage}
                pageSize={pageSize}
                totalCount={allSprints.length}
                onPageChange={setCurrentPage}
                setPage={setCurrentPage}
                setPageSize={setPageSize}
                handleSort={handleSort}
            />
        </div>
    );
}

export default SprintsListPage;
